import { useState } from 'react';
import { useSSE, type SseEvent } from '@/hooks/useSSE';
import { cn } from '@/lib/cn';

/**
 * Live SSE indicator for the header. Subscribes to /api/stream/market,
 * shows connection state (green pulse = open, amber = reconnecting) and
 * "last tick" timestamp from the most recent server event.
 *
 * Real price ticks update the timestamp during market hours; the 25s
 * heartbeat keeps it ticking otherwise.
 */
export function LiveStatus() {
  const [lastTickAt, setLastTickAt] = useState<Date | null>(null);
  const [lastInstrument, setLastInstrument] = useState<string | null>(null);

  const status = useSSE('market', {
    handlers: {
      hello: () => setLastTickAt(new Date()),
      heartbeat: () => setLastTickAt(new Date()),
      'price.tick': (e: SseEvent<{ instrumentCode?: string }>) => {
        setLastTickAt(new Date());
        if (e.payload?.instrumentCode) setLastInstrument(e.payload.instrumentCode);
      },
    },
  });

  const label =
    status === 'open' ? 'live' : status === 'connecting' ? 'reconnecting' : 'offline';
  const tickStr = lastTickAt
    ? lastTickAt.toLocaleTimeString('en-GB', { hour12: false })
    : '—';

  return (
    <div className="flex items-center gap-2 text-xs text-text-faint" title={lastInstrument ? `last instrument: ${lastInstrument}` : undefined}>
      <span
        className={cn(
          'relative w-2 h-2 rounded-full flex-none',
          status === 'open' && 'bg-kam-green',
          status === 'connecting' && 'bg-kam-amber',
          status === 'closed' && 'bg-kam-red',
        )}
      >
        {status === 'open' && (
          <span
            aria-hidden
            className="absolute inset-[-3px] rounded-full bg-kam-green opacity-35 animate-pulse"
          />
        )}
      </span>
      <span>
        <span className="text-text-dim">{label}</span>
        <span className="mx-1.5 opacity-40">·</span>
        <span className="font-mono">{tickStr}</span>
      </span>
    </div>
  );
}
